import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { RegistrationLayout } from '../registration/RegistrationLayout';
import { PasswordMeter, PasswordRules } from '../../shared/components/PasswordRules';
import { PASSWORD_MIN_LENGTH } from '../../shared/password/policy';
import { ApiError } from '../../shared/api/problem';
import { acceptInvitation, getInvitationByToken } from './api';

/**
 * Where the link in an invitation email lands.
 *
 * THE OTHER HALF OF "ADD A USER". A tenant administrator does not create an account and hand over a
 * password — they send an invitation, and the person named on it sets their own. Without this
 * screen the invite endpoints emailed a URL that resolved to nothing, which is what they did from
 * m3.7 until now.
 *
 * The email address is fixed by the invitation and shown, not typed: the token was issued for one
 * address, and letting somebody edit it here would suggest they could accept as anybody. Only the
 * name and the password come from the person accepting. The groups they will land in were chosen
 * by whoever invited them and are applied server-side, so nothing about them is in this payload.
 *
 * Accepting does NOT sign them in — the server issues no session — so it ends at the login screen.
 */
/**
 * What a 409 on an invitation actually means.
 *
 * The server sends one for a cancelled invitation and one for an already-accepted invitation, and
 * the reader's next move differs: ask for a new one, or just sign in. Its sentence says which, so
 * use it; the fallback only covers a body that arrived without one.
 */
function describe409(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  return err.problem.detail ?? err.problem.title ?? fallback;
}

export function AcceptRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams({ strict: false }) as { token: string };

  const invitation = useQuery({
    queryKey: ['invitation', token] as const,
    queryFn: ({ signal }) => getInvitationByToken(token, signal),
    retry: false,
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () => acceptInvitation(token, password, firstName.trim(), lastName.trim()),
    onSuccess: () => void navigate({ to: '/login' }),
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 404) return setSubmitError(t('invitation.errors.notFound'));
        if (err.status === 410) return setSubmitError(t('invitation.errors.expired'));
        // 409 covers two different events — withdrawn and already accepted — and they send the
        // reader somewhere different, so the server's own sentence wins over a guess. Saying
        // "you already accepted this, go and sign in" to somebody whose invitation was CANCELLED
        // points them at an account that does not exist.
        if (err.status === 409) return setSubmitError(describe409(err, t('invitation.errors.alreadyUsed')));
        // A rejected password is the server's own sentence too, and it names the rule that failed.
        const detail = err.problem.detail ?? err.problem.title;
        if (detail) return setSubmitError(detail);
      }
      setSubmitError(t('invitation.errors.generic'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = t('invitation.errors.firstNameRequired');
    if (!lastName.trim()) next.lastName = t('invitation.errors.lastNameRequired');
    if (!password) next.password = t('invitation.errors.passwordRequired');
    else if (password.length < PASSWORD_MIN_LENGTH)
      next.password = t('invitation.errors.passwordTooShort', { min: PASSWORD_MIN_LENGTH });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    accept.mutate();
  }

  if (invitation.isLoading) {
    return (
      <RegistrationLayout>
        <Text size="sm" c="dimmed">
          {t('shell.loading')}
        </Text>
      </RegistrationLayout>
    );
  }

  if (invitation.isError) {
    const status = invitation.error instanceof ApiError ? invitation.error.status : 0;
    const message =
      status === 410
        ? t('invitation.errors.expired')
        : status === 409
          ? describe409(invitation.error, t('invitation.errors.alreadyUsed'))
          : t('invitation.errors.notFound');
    return (
      <RegistrationLayout>
        <Stack gap="md">
          <Title order={2}>{t('invitation.deadTitle')}</Title>
          <Text size="sm" c="dimmed">
            {message}
          </Text>
          {/* Somebody whose invitation has already been used almost certainly has an account. */}
          <Button color="invetrak" onClick={() => void navigate({ to: '/login' })}>
            {t('invitation.goToSignIn')}
          </Button>
        </Stack>
      </RegistrationLayout>
    );
  }

  const invite = invitation.data!;

  return (
    <RegistrationLayout>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>{t('invitation.title', { tenant: invite.tenantDisplayName })}</Title>
          <Text size="sm" c="dimmed">
            {t('invitation.subtitle', { email: invite.email })}
          </Text>
        </Stack>

        <form onSubmit={onSubmit} noValidate>
          <Stack gap="md">
            {/* SimpleGrid, not a grown Group: `grow` divides the row no matter how narrow it
                gets, which on a 375px screen left two 131px boxes rather than stacking them. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label={t('invitation.firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
                error={errors.firstName}
                maxLength={100}
                autoComplete="given-name"
                required
              />
              <TextInput
                label={t('invitation.lastName')}
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
                error={errors.lastName}
                maxLength={100}
                autoComplete="family-name"
                required
              />
            </SimpleGrid>

            <PasswordInput
              label={t('invitation.password')}
              placeholder={t('registration.complete.passwordHint')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              error={errors.password}
              autoComplete="new-password"
              required
            />

            <PasswordMeter password={password} />
            <PasswordRules password={password} />

            {submitError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {submitError}
              </Alert>
            )}

            <Button type="submit" color="invetrak" size="md" fullWidth loading={accept.isPending}>
              {t('invitation.submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </RegistrationLayout>
  );
}
